import type { ButtonProps } from "front-commons/ds/components/Button/interfaces";
import type { FlexProps } from "front-commons/ds/components/Flex/interfaces";
import type { WatchInputParams } from "front-commons/hooks/useForm/interfaces";
import type { TextareaHTMLAttributes } from "react";
import type { CreateQuizResponse } from "services/customer/interfaces";
import type { PosLinkValidateRequest } from "services/pos/interfaces";
import type { RoleTypes } from "stores/pos/interfaces";

export interface PosRequestLintContextProps {
	percentage?: number;
	quizCreateResponse?: CreateQuizResponse;
	loadings: {
		createQuizLoading: boolean;
		posLinkStatusLoading: boolean;
		posLinkRequestLoading: boolean;
	};
	serasaStatusLoading: boolean;
	linkSummary: LinkSummaryParams;
	currentStep?: StepTypes;
	navigateHandler: NavigationHandlerParams;
	exceptionHandler: ExceptionHandlerParams;
	role?: RoleTypes;
	CNPJHandler: CNPJHandlerParams;
	roleHandler: RoleHandler;
}

export type ExceptionTypes = 'CNPJ' | 'BLOCKED' | 'LAST_TRY' | 'SERVICE_UNAVAILABLE';
export type StepTypes = 'CNPJ_INPUT' | 'CUSTOMER_VALIDATION' | 'REQUEST_RESULT';

type CNPJsExtendedTypes = "PENDING_VALIDATION" | "FIXED";

export type CNPJsLinkRequestStatusTypes = "INVALID_CNPJ" | "POS_UNAVAILABLE" | "ALREADY_RELATED_APPROVED" | "ALREADY_RELATED_PENDING" | "ALREADY_RELATED_BLOCKED" | "ALREADY_RELATED_REJECTED" | "NO_APPROVER" | "VALID" | CNPJsExtendedTypes;

export type CNPJsLinkStatusTypes = "SERVICE_UNAVAILABLE" | "ACCESS_REQUESTED" | "ACCESS_GRANTED" | "ACCESS_GRANTED_MASTER" | Extract<CNPJsLinkRequestStatusTypes, 'NO_APPROVER'>;

export interface ExceptionHandlerParams {
	get: ExceptionTypes | undefined;
	set(newException: ExceptionTypes): void;
	clear(): void;
}

export interface NavigationHandlerParams {
	next(POSLinkRequest?: string[]): void;
	abort(openPendingLinkRequest?: boolean): void;
	reset(): void;
	tryAgain(): void;
}

export interface CNPJHandlerParams {
	raw: RequestCNPJsParams[];
	set: React.Dispatch<React.SetStateAction<RequestCNPJsParams[]>>
	get(replaceData?: RequestCNPJsParams[]): {
		valid: string[];
		withException: RequestCNPJsParams[];
	}
	edit(index: number, value: string): void;
}

export interface RoleHandler {
	readonly get: RoleTypes | undefined;
	set: React.Dispatch<React.SetStateAction<RoleTypes | undefined>>;
}

export interface RequestCNPJsParams {
	cnpj: string;
	index: number;
	status: CNPJsLinkRequestStatusTypes;
}

export interface LinkSummaryParams {
	NO_APPROVER: string[];
	ACCESS_GRANTED: string[];
	ACCESS_REQUESTED: string[];
	SERVICE_UNAVAILABLE: string[];
	ACCESS_GRANTED_MASTER: string[];
	total: number;
}

export interface StepSupportTitleProps {
	title: string;
	description?: string;
}

export interface StepSupportCTAProps {
	help: Partial<ButtonProps>;
	dialog: {
		title: string;
		content: React.ReactNode;
	}
	primary: Partial<ButtonProps>;
	secondary?: Partial<ButtonProps>;
	containerProps?: Partial<FlexProps>;
}

interface StepsLoadingCommonProps {
	loadings: {
		createQuizLoading: boolean;
		posLinkStatusLoading: boolean;
		posLinkRequestLoading: boolean;
	}
}

//! CNPJ STEP

export interface ConflictStepHelperProps {
	exceptionData: RequestCNPJsParams[]
}

export interface CNPJStepCommonsProps {
	loadings: {
		validatingLoading: boolean;
	} & StepsLoadingCommonProps['loadings'];
	handleValidateCNPJs(params?: Partial<PosLinkValidateRequest>): Promise<void>
}

export interface TextareaProps {
	native: TextareaHTMLAttributes<string>;
	counter: number;
	percentage?: number;
}

export interface RoleCardProps {
	label: string;
	value: RoleTypes;
	checked: boolean;
	description: string;
	onClick(value: RoleTypes): void;
}

export interface EditableContentProps extends Pick<RequestCNPJsParams, 'index' | 'status'> {
	value: string;
	editable: boolean;
	handleEditCNPJ: CNPJHandlerParams['edit'];
}

//! VALIDATION STEP


export interface QuestionProps {
	id: string;
	question: string;
	answers: GenericLabelValue[];
}

export interface QuizProps {
	answers?: QuestionProps['answers'];
	loading?: 'skeleton' | 'validating';
	questions?: QuestionProps[];
	exception?: ExceptionTypes;
	currentResponse: any;
	currentIndexQuestion: number;
	currentQuestionPosition: number;
	handleAnswerSelect({ currentTarget: { value } }: WatchInputParams): void;
}


//! LINK STEP
export interface RequestLinkProps {
	Success: {
		total: number;
		hasPendingLink: boolean;
		CTA: {
			children: string;
			onClick(): void;
		};
	};
	Table: {
		data: Record<'NO_APPROVER' | 'SERVICE_UNAVAILABLE', string[]>;
		total: number;
		failed: number;
		succeed: number;
		CTA: {
			children: string;
			onClick(): void;
		};
	};
	Failed: {
		CTA: {
			children: string;
			onClick(): void;
		};
	};
}

export type ResultType = {
	type: keyof RequestLinkProps;
	data: RequestLinkProps[keyof RequestLinkProps];
};